import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { Plus, Edit2, Trash2, Upload, X, Star } from "lucide-react";

const EMPTY = {
  name: "", slug: "", short_description: "", description: "",
  category: "akrilik", volume: "500ml", colors: [], features: [],
  image_url: "", gallery: [], price: 0, price_info: "", is_featured: false, in_stock: true,
  seo_title: "", seo_description: "",
};

function slugify(s) {
  return s.toLowerCase()
    .replace(/ı/g,"i").replace(/ğ/g,"g").replace(/ü/g,"u").replace(/ş/g,"s").replace(/ö/g,"o").replace(/ç/g,"c")
    .replace(/[^a-z0-9]+/g,"-").replace(/^-+|-+$/g,"");
}

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/products").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEdit(null); setOpen(true); };
  const openEdit = (p) => {
    setEdit(p);
    setForm({ ...EMPTY, ...p, colors: p.colors || [], features: p.features || [], gallery: p.gallery || [] });
    setOpen(true);
  };

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e, target = "image_url") => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${API_BASE.replace("/api","")}${data.url}`;
      if (target === "image_url") setF("image_url", url);
      else setF("gallery", [...(form.gallery || []), url]);
      toast.success("Görsel yüklendi");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      slug: form.slug || slugify(form.name),
      price: Number(form.price) || 0,
      colors: typeof form.colors === "string" ? form.colors.split(",").map((s) => s.trim()).filter(Boolean) : form.colors,
      features: typeof form.features === "string" ? form.features.split("\n").map((s) => s.trim()).filter(Boolean) : form.features,
    };
    try {
      if (edit) await api.put(`/products/${edit.id}`, payload);
      else await api.post("/products", payload);
      toast.success(edit ? "Ürün güncellendi" : "Ürün eklendi");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`"${p.name}" silinsin mi?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Silindi");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="wb-overline">Yönetim</div>
          <h1 className="wb-h2 mt-2">Ürünler</h1>
        </div>
        <button onClick={openNew} className="wb-btn-primary" data-testid="admin-product-new">
          <Plus size={16} /> Yeni Ürün
        </button>
      </div>

      <div className="wb-card mt-8 overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left text-[#a0aabf] text-xs uppercase tracking-widest">
              <th className="p-4">Görsel</th>
              <th className="p-4">Ad</th>
              <th className="p-4">Kategori</th>
              <th className="p-4">Hacim</th>
              <th className="p-4">Fiyat</th>
              <th className="p-4">Öne Çıkan</th>
              <th className="p-4 text-right">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="7" className="p-8 text-center text-[#a0aabf]">Henüz ürün eklenmedi.</td></tr>
            ) : items.map((p) => (
              <tr key={p.id} className="border-b border-white/5" data-testid={`row-product-${p.slug}`}>
                <td className="p-4">
                  <div className="w-14 h-14 bg-[#0c1f44] overflow-hidden">
                    {p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                </td>
                <td className="p-4">
                  <div>{p.name}</div>
                  <div className="text-xs text-[#a0aabf]">/{p.slug}</div>
                </td>
                <td className="p-4 text-[#a0aabf]">{p.category}</td>
                <td className="p-4 text-[#a0aabf]">{p.volume}</td>
                <td className="p-4 text-[#d4af37]">{Number(p.price) > 0 ? `${Number(p.price).toFixed(2)} TL` : "-"}</td>
                <td className="p-4">{p.is_featured ? <Star size={16} className="text-[#d4af37]" /> : "-"}</td>
                <td className="p-4 text-right space-x-2">
                  <button onClick={() => openEdit(p)} className="p-2 border border-white/10 hover:border-[#d4af37] hover:text-[#d4af37]" data-testid={`edit-${p.slug}`}><Edit2 size={14} /></button>
                  <button onClick={() => remove(p)} className="p-2 border border-white/10 hover:border-red-500 hover:text-red-400" data-testid={`delete-${p.slug}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-[#081b3d] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-white/10 sticky top-0 bg-[#081b3d]">
              <h2 className="font-serif text-xl">{edit ? "Ürünü Düzenle" : "Yeni Ürün"}</h2>
              <button onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4" data-testid="product-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input className="wb-input" placeholder="Ürün Adı" required value={form.name} onChange={(e) => { setF("name", e.target.value); if (!edit) setF("slug", slugify(e.target.value)); }} data-testid="form-name" />
                <input className="wb-input" placeholder="slug (url)" required value={form.slug} onChange={(e) => setF("slug", e.target.value)} data-testid="form-slug" />
              </div>
              <input className="wb-input" placeholder="Kısa açıklama" required value={form.short_description} onChange={(e) => setF("short_description", e.target.value)} data-testid="form-short" />
              <textarea className="wb-input min-h-[140px]" placeholder="Detaylı açıklama" value={form.description} onChange={(e) => setF("description", e.target.value)} data-testid="form-desc" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <input className="wb-input" placeholder="Kategori" value={form.category} onChange={(e) => setF("category", e.target.value)} />
                <input className="wb-input" placeholder="Hacim (örn 500ml)" value={form.volume} onChange={(e) => setF("volume", e.target.value)} />
                <input className="wb-input" type="number" step="0.01" min="0" placeholder="Fiyat (TL, KDV dahil)" value={form.price} onChange={(e) => setF("price", e.target.value)} data-testid="form-price" />
              </div>
              <input className="wb-input" placeholder="Fiyat etiketi (price=0 ise gösterilir, opsiyonel)" value={form.price_info || ""} onChange={(e) => setF("price_info", e.target.value)} />
              <input
                className="wb-input"
                placeholder="Renkler (hex, virgülle ayır: #ff0000, #00ff00)"
                value={Array.isArray(form.colors) ? form.colors.join(", ") : form.colors}
                onChange={(e) => setF("colors", e.target.value)}
              />
              <textarea
                className="wb-input min-h-[100px]"
                placeholder="Özellikler (her satıra bir özellik)"
                value={Array.isArray(form.features) ? form.features.join("\n") : form.features}
                onChange={(e) => setF("features", e.target.value)}
              />

              <div>
                <label className="text-xs uppercase tracking-widest text-[#a0aabf]">Kapak Görseli</label>
                <div className="mt-2 flex items-center gap-3">
                  {form.image_url && <img src={form.image_url} alt="" className="w-16 h-16 object-cover border border-white/10" />}
                  <label className="wb-btn-secondary cursor-pointer !py-2 !px-4 text-xs">
                    <Upload size={14} /> {uploading ? "Yükleniyor..." : "Yükle"}
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e, "image_url")} data-testid="upload-image" />
                  </label>
                  {form.image_url && <button type="button" className="text-xs text-red-400" onClick={() => setF("image_url", "")}>Kaldır</button>}
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-[#a0aabf]">Galeri</label>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  {(form.gallery || []).map((g, i) => (
                    <div key={i} className="relative">
                      <img src={g} alt="" className="w-16 h-16 object-cover border border-white/10" />
                      <button type="button" onClick={() => setF("gallery", form.gallery.filter((_, idx) => idx !== i))} className="absolute -top-2 -right-2 bg-red-500 w-5 h-5 flex items-center justify-center text-xs">×</button>
                    </div>
                  ))}
                  <label className="wb-btn-secondary cursor-pointer !py-2 !px-4 text-xs">
                    <Upload size={14} /> Ekle
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e, "gallery")} />
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input className="wb-input" placeholder="SEO Başlık" value={form.seo_title || ""} onChange={(e) => setF("seo_title", e.target.value)} />
                <input className="wb-input" placeholder="SEO Açıklama" value={form.seo_description || ""} onChange={(e) => setF("seo_description", e.target.value)} />
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_featured} onChange={(e) => setF("is_featured", e.target.checked)} data-testid="form-featured" />
                Öne Çıkan Ürün
              </label>

              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <button type="button" onClick={() => setOpen(false)} className="wb-btn-secondary">İptal</button>
                <button type="submit" className="wb-btn-primary" data-testid="form-submit">{edit ? "Güncelle" : "Kaydet"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
